import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck, X, Zap, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

interface PremiumModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  featureDescription: string;
}

export const PremiumModal: React.FC<PremiumModalProps> = ({ 
  isOpen, 
  onClose, 
  featureName, 
  featureDescription 
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-gray-950/80 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-gray-900 border border-gray-800 rounded-3xl overflow-hidden shadow-2xl"
          >
            {/* Header / Banner */}
            <div className="h-24 bg-gradient-to-br from-indigo-600 to-indigo-900 relative">
              <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white/80 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center border border-gray-800 shadow-xl">
                <ShieldCheck size={32} className="text-indigo-400" />
              </div>
            </div>

            <div className="px-8 pt-12 pb-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-2">Premium Feature</h2>
              <p className="text-indigo-400 font-bold uppercase tracking-widest text-[10px] mb-4">
                Unlock {featureName}
              </p>
              
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                {featureDescription}
              </p>

              <div className="space-y-3 mb-8">
                 {[
                   "Cloud Sync Favorites",
                   "Modern Grid View Layout",
                   "High Quality Recording",
                   "Lifetime Priority Support"
                 ].map((benefit, i) => (
                   <div key={i} className="flex items-center gap-2 text-xs text-gray-300 bg-gray-800/50 p-2 rounded-lg border border-white/5">
                     <CheckCircle2 size={14} className="text-indigo-400 shrink-0" />
                     {benefit}
                   </div>
                 ))}
              </div>

              <div className="flex flex-col gap-3">
                <Link
                  href="/pricing"
                  className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2 group"
                >
                  <Zap size={18} className="fill-white" />
                  Upgrade to Premium
                </Link>
                <button
                  onClick={onClose}
                  className="w-full py-3 text-gray-400 hover:text-white text-sm font-medium transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
